import "../../../css/CatalogSync.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Input, Button, Tag, Spin, message } from "antd";
import { SyncOutlined, SearchOutlined } from "@ant-design/icons";
import {
  getCatalogItems,
  triggerCatalogSync,
  getSyncJobs,
  getRunningSyncJobs,
} from "../../../services/integrations/klaviyoCatalogService";
import JobDetailDrawer from "./JobDetailDrawer";

// ── Helpers ──

const relativeTime = (dateStr) => {
  if (!dateStr) return "-";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const formatDatetime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const computeDuration = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return "-";
  const ms = new Date(completedAt) - new Date(startedAt);
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
};

const statusColor = (status) => {
  if (status === "completed") return "green";
  if (status === "failed") return "red";
  if (status === "in_progress") return "blue";
  return "default";
};

// ── Component ──

export default function CatalogSyncTab({ customerId }) {
  // Status strip
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);

  // Job history
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [drawerJob, setDrawerJob] = useState(null);

  // Catalog items
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsPage, setItemsPage] = useState({ number: 0, size: 20, totalElements: 0 });
  const [search, setSearch] = useState("");
  const searchTimerRef = useRef(null);
  const pollRef = useRef(null);

  // ── Data fetching ──

  const fetchJobs = useCallback(async () => {
    try {
      const data = await getSyncJobs();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent
    }
    setJobsLoading(false);
  }, []);

  const fetchItems = useCallback(async (page = 0, size = 20, searchTerm = "") => {
    setItemsLoading(true);
    try {
      const data = await getCatalogItems(page, size, searchTerm);
      setItems(data.content || []);
      setItemsPage(data.page || { number: 0, size: 20, totalElements: 0 });
    } catch (e) {
      // silent
    }
    setItemsLoading(false);
  }, []);

  const checkRunning = useCallback(async () => {
    try {
      const running = await getRunningSyncJobs();
      if (running && running.length > 0) {
        setSyncing(true);
        setSyncProgress(running[0]);
      } else {
        if (syncing) {
          // was syncing, now done
          setSyncing(false);
          setSyncProgress(null);
          fetchJobs();
          fetchItems(itemsPage.number, itemsPage.size, search);
        }
      }
    } catch (e) {
      // silent
    }
  }, [syncing, fetchJobs, fetchItems, itemsPage.number, itemsPage.size, search]);

  // ── Initial load ──

  useEffect(() => {
    fetchJobs();
    fetchItems(0, 20, "");
    // Check if already syncing
    const checkInitial = async () => {
      try {
        const running = await getRunningSyncJobs();
        if (running && running.length > 0) {
          setSyncing(true);
          setSyncProgress(running[0]);
        }
      } catch (e) {
        // silent
      }
    };
    checkInitial();
  }, [fetchJobs, fetchItems]);

  // ── Polling ──

  useEffect(() => {
    if (syncing) {
      pollRef.current = setInterval(checkRunning, 3000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncing, checkRunning]);

  // ── Sync Now ──

  const handleSyncNow = async () => {
    if (!customerId) {
      message.error("No customer ID available");
      return;
    }
    try {
      setSyncing(true);
      await triggerCatalogSync(customerId);
      // Sync may be synchronous (v1), refresh everything
      setSyncing(false);
      fetchJobs();
      fetchItems(itemsPage.number, itemsPage.size, search);
      message.success("Catalog sync completed");
    } catch (e) {
      setSyncing(false);
      message.error("Failed to start catalog sync");
    }
  };

  // ── Search with debounce ──

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchItems(0, itemsPage.size, val);
    }, 500);
  };

  // Cleanup search timer
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // ── Table pagination ──

  const handleTableChange = (pagination) => {
    fetchItems(pagination.current - 1, pagination.pageSize, search);
  };

  // ── Latest job for status strip ──

  const latestJob = jobs.length > 0 ? jobs[0] : null;

  // ── Job history columns ──

  const jobColumns = [
    {
      title: "Started",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (val) => formatDatetime(val),
    },
    {
      title: "Trigger",
      dataIndex: "triggerSource",
      key: "triggerSource",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={statusColor(val)}>{val}</Tag>,
    },
    {
      title: "Inserted",
      dataIndex: "itemsInserted",
      key: "itemsInserted",
    },
    {
      title: "Updated",
      dataIndex: "itemsUpdated",
      key: "itemsUpdated",
    },
    {
      title: "Errored",
      dataIndex: "itemsErrored",
      key: "itemsErrored",
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) => computeDuration(record.startedAt, record.completedAt),
    },
  ];

  // ── Catalog items columns ──

  const itemColumns = [
    {
      title: "Vendor Item ID",
      dataIndex: "sourceItemId",
      key: "sourceItemId",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Last Synced",
      dataIndex: "lastSyncedAt",
      key: "lastSyncedAt",
      render: (val) => relativeTime(val),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
    },
  ];

  return (
    <div>
      {/* Section 1: Status Strip */}
      <div className="catalog-sync-strip">
        {syncing ? (
          <div className="catalog-sync-strip-content">
            <Spin size="small" />
            <span>
              Currently syncing...
              {syncProgress && (
                <> {syncProgress.itemsInserted} inserted, {syncProgress.itemsUpdated} updated</>
              )}
            </span>
          </div>
        ) : (
          <div className="catalog-sync-strip-content">
            <span>
              Last synced: {latestJob ? relativeTime(latestJob.completedAt || latestJob.startedAt) : "never"}
              {" \u2022 "}
              {itemsPage.totalElements} items in catalog
            </span>
            <Button
              type="primary"
              className="primary-orange-button"
              onClick={handleSyncNow}
              icon={<SyncOutlined />}
            >
              Sync Now
            </Button>
          </div>
        )}
      </div>

      {/* Section 2: Job History */}
      <div className="catalog-sync-section">
        <div className="catalog-sync-section-title">Job History</div>
        <Table
          dataSource={jobs.slice(0, 10)}
          columns={jobColumns}
          rowKey="id"
          loading={jobsLoading}
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => setDrawerJob(record),
            style: { cursor: "pointer" },
          })}
        />
      </div>

      {/* Section 3: Catalog Items Browser */}
      <div className="catalog-sync-section">
        <div className="catalog-sync-section-header">
          <div className="catalog-sync-section-title">
            Synced Catalog Items ({itemsPage.totalElements})
          </div>
          <Input
            placeholder="Search items..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={handleSearchChange}
            style={{ width: 240 }}
            allowClear
          />
        </div>
        <Table
          dataSource={items}
          columns={itemColumns}
          rowKey="id"
          loading={itemsLoading}
          size="small"
          pagination={{
            current: itemsPage.number + 1,
            pageSize: itemsPage.size,
            total: itemsPage.totalElements,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* Job Detail Drawer */}
      <JobDetailDrawer
        job={drawerJob}
        open={!!drawerJob}
        onClose={() => setDrawerJob(null)}
      />
    </div>
  );
}
